import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import { workflows } from "@circle-react/helpers/urlHelpers/internalApi/workflows";

import type {
  Action,
  CommunityCurrentUsageStats,
  ListWorkflows,
  Trigger,
} from "../components/SettingsApp/Workflows/generated/WorkflowsApi.types";
import type {
  ActionWithDefinedType,
  WorkflowGraphPayload,
  WorkflowGraphResponse,
} from "../components/SettingsApp/Workflows/types";
import type {
  BodyParamsProps,
  BodyProps,
  IdBodyProps,
  IdProps,
  ParamsProps,
  TaskIdProps,
  TemplateIdProps,
  TemplateIdWithOverridesProps,
  TemplateKeyProps,
  WorkflowIdBodyProps,
  WorkflowIdGraphBodyProps,
  WorkflowIdIdBodyProps,
  WorkflowIdIdProps,
  WorkflowIdProps,
  WorkflowIdRunIdProps,
} from "./workflowsApi.types";

const list = ({ params = {} }: ParamsProps) =>
  reactQueryGet<ListWorkflows>(workflows.list({ params }));
const listHistory = ({ params = {} }: ParamsProps) =>
  reactQueryGet(workflows.tasks({ params }));
const taskDetail = ({ taskId, params = {} }: TaskIdProps) =>
  reactQueryGet(workflows.taskDetail({ params, taskId }));
const create = ({ params = {}, body = {} }: BodyParamsProps) =>
  reactQueryPost(workflows.create({ params }), body);
const fetch = ({ id, params = {} }: IdProps) =>
  reactQueryGet(workflows.fetch({ id, params }));
const update = ({ id, params = {}, body = {} }: IdBodyProps) =>
  reactQueryPut(workflows.update({ id, params }), body);
const activate = ({ id, params = {} }: IdProps) =>
  reactQueryPut<undefined, object>(workflows.activate({ id, params }));
const inactivate = ({ id, params = {} }: IdProps) =>
  reactQueryPut<undefined, object>(workflows.inactivate({ id, params }));

const createTrigger = ({
  workflowId,
  params = {},
  body = {},
}: WorkflowIdBodyProps) =>
  reactQueryPost<Trigger>(
    workflows.createTrigger({ workflowId, params }),
    body,
  );

const updateTrigger = ({
  workflowId,
  id,
  params = {},
  body = {},
}: WorkflowIdIdBodyProps) =>
  reactQueryPut<Trigger, any>(
    workflows.updateTrigger({ workflowId, id, params }),
    body,
  );

const deleteTrigger = ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
  reactQueryDelete(workflows.deleteTrigger({ workflowId, id, params }));

const createRun = ({ workflowId, params = {} }: WorkflowIdProps) =>
  reactQueryPost<undefined>(workflows.createRun({ workflowId, params }));

const fetchRuns = ({ workflowId, params = {} }: WorkflowIdProps) =>
  reactQueryGet(workflows.fetchRuns({ workflowId, params }));

const fetchRun = ({ workflowId, runId, params = {} }: WorkflowIdRunIdProps) =>
  reactQueryGet(workflows.fetchRun({ workflowId, runId, params }));

const cancelRun = ({ workflowId, runId, params = {} }: WorkflowIdRunIdProps) =>
  reactQueryPut(workflows.cancelRun({ workflowId, runId, params }));

const fetchTasksInRun = ({
  workflowId,
  runId,
  params = {},
}: WorkflowIdRunIdProps) =>
  reactQueryGet(workflows.fetchTasksInRun({ workflowId, runId, params }));

const createAudience = ({
  workflowId,
  params = {},
  body = {},
}: WorkflowIdBodyProps) =>
  reactQueryPost(workflows.createAudience({ workflowId, params }), body);

const updateAudience = ({
  workflowId,
  id,
  params = {},
  body = {},
}: WorkflowIdIdBodyProps) =>
  reactQueryPut(workflows.updateAudience({ workflowId, id, params }), body);

const createAction = ({
  workflowId,
  params = {},
  body = {},
}: WorkflowIdBodyProps) =>
  reactQueryPost<Action>(workflows.createAction({ workflowId, params }), body);

const updateAction = ({
  workflowId,
  id,
  params = {},
  body = {},
}: WorkflowIdIdBodyProps) =>
  reactQueryPut<Action, any>(
    workflows.updateAction({ workflowId, id, params }),
    body,
  );

const deleteAction = ({ workflowId, id, params = {} }: WorkflowIdIdProps) =>
  reactQueryDelete<undefined, object>(
    workflows.deleteAction({ workflowId, id, params }),
  );

const upsertActions = ({
  workflowId,
  params = {},
  body = {},
}: WorkflowIdBodyProps) =>
  reactQueryPost<ActionWithDefinedType[]>(
    workflows.upsertActions({ workflowId, params }),
    body,
  );

// PUT, not POST: the graph endpoint replaces the workflow's whole action+edge
// graph rather than appending to it.
const updateGraph = ({
  workflowId,
  params = {},
  body,
}: WorkflowIdGraphBodyProps) =>
  reactQueryPut<WorkflowGraphResponse, WorkflowGraphPayload>(
    workflows.updateGraph({ workflowId, params }),
    body,
  );

const createOneTimeSchedule = ({
  workflowId,
  params = {},
  body = {},
}: WorkflowIdBodyProps) =>
  reactQueryPost(workflows.createOneTimeSchedule({ workflowId, params }), body);

const createRecurringSchedule = ({
  workflowId,
  params = {},
  body = {},
}: WorkflowIdBodyProps) =>
  reactQueryPost(
    workflows.createRecurringSchedule({ workflowId, params }),
    body,
  );

const updateOneTimeSchedule = ({
  workflowId,
  id,
  params = {},
  body = {},
}: WorkflowIdIdBodyProps) =>
  reactQueryPut(
    workflows.updateOneTimeSchedule({ workflowId, id, params }),
    body,
  );

const updateRecurringSchedule = ({
  workflowId,
  id,
  params = {},
  body = {},
}: WorkflowIdIdBodyProps) =>
  reactQueryPut(
    workflows.updateRecurringSchedule({ workflowId, id, params }),
    body,
  );

const reschedule = ({
  workflowId,
  id,
  params = {},
  body = {},
}: WorkflowIdIdBodyProps) =>
  reactQueryPut(workflows.reschedule({ workflowId, id, params }), body);

const templates = ({ params = {} }: ParamsProps) =>
  reactQueryGet(workflows.templates({ params }));

const fetchTemplate = ({ templateId, params = {} }: TemplateIdProps) =>
  reactQueryGet(workflows.fetchTemplate({ templateId, params }));

const createFromTemplate = ({
  templateId,
  params = {},
  overrides = {},
}: TemplateIdWithOverridesProps) =>
  reactQueryPost(
    workflows.createFromTemplate({ templateId, params }),
    overrides,
  );

const createFromTemplateKey = ({ templateKey }: TemplateKeyProps) =>
  reactQueryPost<{ id: string }>(
    workflows.createFromTemplateKey({ templateKey }),
  );

const archiveWorkflow = ({ id, params = {} }: IdProps) =>
  reactQueryPut(workflows.archive({ id, params }));

const duplicateWorkflow = ({ id, params = {} }: IdProps) =>
  reactQueryPut(workflows.duplicate({ id, params }));

const deleteWorkflow = ({ id, params = {} }: IdProps) =>
  reactQueryDelete(workflows.delete({ id, params }));

const fetchCommunityCurrentUsageStats = ({ params = {} } = {}) =>
  reactQueryGet<CommunityCurrentUsageStats>(
    workflows.fetchCommunityCurrentUsageStats({ params }),
  );

const fetchPreviewAttributes = ({ params = {} }: ParamsProps) =>
  reactQueryGet(workflows.previewMessages.previewAttributes({ params }));

const fetchInterpolatedPreviewMessage = ({ body = {} }: BodyProps) =>
  reactQueryPost(workflows.fetchInterpolatedMessage({}), body);

const testWebhook = ({ body = {} }: BodyProps) =>
  reactQueryPost(workflows.testWebhook({}), body);

const fetchContacts = ({ params = {} }: ParamsProps) =>
  reactQueryGet(workflows.contacts.index({ params }));

const fetchDynamicVariables = ({ params = {} }: ParamsProps) =>
  reactQueryGet(workflows.fetchDynamicVariables({ params }));

export const workflowsApi = {
  list,
  listHistory,
  taskDetail,
  create,
  update,
  fetch,
  activate,
  inactivate,
  createTrigger,
  updateTrigger,
  deleteTrigger,
  createRun,
  fetchRuns,
  fetchRun,
  fetchTasksInRun,
  createAudience,
  updateAudience,
  createAction,
  updateAction,
  deleteAction,
  upsertActions,
  updateGraph,
  createOneTimeSchedule,
  updateOneTimeSchedule,
  createRecurringSchedule,
  updateRecurringSchedule,
  reschedule,
  templates,
  fetchTemplate,
  createFromTemplate,
  createFromTemplateKey,
  archiveWorkflow,
  deleteWorkflow,
  fetchCommunityCurrentUsageStats,
  duplicateWorkflow,
  fetchPreviewAttributes,
  fetchInterpolatedPreviewMessage,
  testWebhook,
  cancelRun,
  fetchContacts,
  fetchDynamicVariables,
};
